import { createApi } from "@reduxjs/toolkit/query/react";
import { apiFetchBaseQuery } from "../apiFetchBaseQuery";
import {
	DefaultHierarchyTargetDataState,
	GetTargetAudienceResponse,
	GetHierarchyDatasetResponse,
	GetTrafficAudienceListResponse,
} from "@/types/TrafficEstimator";
import {
	UpsertAudiencePayload,
	CreateOrUpdateAudienceResponse,
	GeoLookupFormPayload,
	GeoPopulationResponse,
	GetRegionsByStates,
} from "@/types/Spatial";
import { PaginationParams } from "@/types/Pagination";

export const defaultHierarchyCategoriesSelectionState: DefaultHierarchyTargetDataState = {
	target_household: {
		categoryName: "",
		subCategories: {},
	},
	target_individual: {
		categoryName: "",
		subCategories: {},
	},
	target_dwelling: {
		categoryName: "",
		subCategories: {},
	},
	target_lifestyle: {
		categoryName: "",
		subCategories: {},
	},
	target_interest: {
		categoryName: "",
		subCategories: {},
	},
};

const makeFormData = (formData: Record<string, unknown>) => {
	const formDataPayload = formData instanceof FormData ? formData : new FormData();
	if (!(formData instanceof FormData)) {
		Object.entries(formData).forEach(([key, value]) => {
			formDataPayload.append(key, Array.isArray(value) ? value.join(",") : (value as string));
		});
	}
	return formDataPayload;
};

export const trafficEstimatorApiSlice = createApi({
	reducerPath: "trafficEstimatorApiSlice",
	baseQuery: apiFetchBaseQuery,
	tagTypes: [
		"hierarchyData",
		"RegionsByStates",
		"getPopulation",
		"fetchAudience",
		"upsetAudience",
		"fetchAudienceList",
	],
	endpoints: builder => ({
		getHierarchyDataset: builder.query<GetHierarchyDatasetResponse, null>({
			query: () => ({
				url: "traffic-estimator/hierarchy/dataset",
			}),
			providesTags: ["hierarchyData"],
		}),
		getRegionsByStates: builder.mutation<GetRegionsByStates, { states: string[]; region_type: string }>({
			query: payload => {
				const { states, region_type } = payload;

				if (!states || !region_type) {
					throw new Error("Missing required fields: 'states' or 'region_type'.");
				}

				const formDataPayload = new FormData();
				const allStates = Array.isArray(states) ? states : [states];
				formDataPayload.append("region_type", region_type);
				allStates.forEach(state => formDataPayload.append("states[]", state));

				return {
					url: "/dooh/regions-by-states",
					method: "POST",
					body: formDataPayload,
					headers: {
						accept: "application/json",
					},
				};
			},
			invalidatesTags: ["RegionsByStates"],
		}),
		getPopulationCount: builder.mutation<GeoPopulationResponse, GeoLookupFormPayload>({
			query: formData => {
				return {
					url: "traffic-estimator/population",
					method: "POST",
					body: makeFormData(formData),
				};
			},
		}),
		getTargetAudience: builder.query<GetTargetAudienceResponse, { id: number }>({
			query: ({ id }) => ({
				url: `traffic-estimator/${id}`,
			}),
			providesTags: ["fetchAudience"],
		}),
		upsertAudience: builder.mutation<CreateOrUpdateAudienceResponse, UpsertAudiencePayload>({
			query: formData => {
				let url = "traffic-estimator/builder";
				let method = "POST";
				if (formData?.id) {
					url += `/${formData.id}`;
					method = "PUT";
				}

				const urlEncodedPayload = new URLSearchParams();
				Object.entries(formData).forEach(([key, value]) => {
					urlEncodedPayload.append(key, String(value));
				});

				return {
					url,
					method,
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: urlEncodedPayload.toString(),
				};
			},
			invalidatesTags: ["upsetAudience"],
		}),
		getTargetAudienceList: builder.query<GetTrafficAudienceListResponse, PaginationParams>({
			query: ({ pageNo, pageSize }) => ({
				url: `traffic-estimator/list?page_no=${pageNo}&limit=${pageSize}`,
			}),
			providesTags: ["fetchAudienceList"],
		}),
		getAudienceTargetingReport: builder.query<Blob, { id: number }>({
			query: ({ id }) => {
				return {
					url: `traffic-estimator/report/${id}`,
					method: "GET",
					responseHandler: response => response.blob(),
				};
			},
		}),
	}),
});

export const {
	useGetHierarchyDatasetQuery,
	useGetRegionsByStatesMutation,
	useGetPopulationCountMutation,
	useUpsertAudienceMutation,
	useGetTargetAudienceQuery,
	useLazyGetTargetAudienceQuery,
	useGetTargetAudienceListQuery,
	useLazyGetAudienceTargetingReportQuery,
} = trafficEstimatorApiSlice;
